import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Collapse,
  Divider,
  CircularProgress,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Menu,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
// import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import toast from 'react-hot-toast';
import { dataAPI, schemasAPI } from '../../services/api';

// Dropdown Field Input Component
const DropdownFieldInput = ({ field, control, errors, schema, onSchemaUpdate }) => {
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isUpdatingSchema, setIsUpdatingSchema] = useState(false);

  const fieldName = field.name;
  const isRequired = field.required;

  const allOptions = field.options || [];

  const handleAddCustomOption = async (newValue, setValue) => {
    if (newValue.trim()) {
      const trimmedValue = newValue.trim();

      // Check if option already exists
      const existingOption = allOptions.find(opt =>
        opt.value === trimmedValue || opt.label === trimmedValue
      );

      if (existingOption) {
        toast.error(`Option "${trimmedValue}" already exists`);
        return;
      }

      try {
        setIsUpdatingSchema(true);

        // Create new option
        const newOption = {
          value: trimmedValue,
          label: trimmedValue,
          isDefault: false
        };

        // Update the schema with the new option
        const updatedFields = schema.fields.map(f => {
          if (f.name === fieldName) {
            return {
              ...f,
              options: [...(f.options || []), newOption]
            };
          }
          return f;
        });

        // Filter out the 'id' field before sending to backend (it's automatically managed)
        const fieldsForBackend = updatedFields.filter(f => f.name !== 'id');

        const updatedSchema = {
          name: schema.name,
          slug: schema.slug,
          description: schema.description,
          websiteId: schema.website._id,
          fields: fieldsForBackend,
          settings: schema.settings
        };

        // Save to backend
        await schemasAPI.updateSchema(schema._id, updatedSchema);

        // Update local schema state
        if (onSchemaUpdate) {
          onSchemaUpdate({
            ...schema,
            fields: updatedFields
          });
        }

        // Set the new value
        setValue(trimmedValue);

        // Reset custom input
        setCustomValue('');
        setShowCustomInput(false);
        setAnchorEl(null);

        // Show success message
        toast.success(`Added "${trimmedValue}" as a new option and updated schema`);

      } catch (error) {
        console.error('Error updating schema:', error);
        toast.error('Failed to add option to schema');
      } finally {
        setIsUpdatingSchema(false);
      }
    }
  };

  const handleShowCustomInput = (event) => {
    setAnchorEl(event.currentTarget);
    setShowCustomInput(true);
  };

  const handleCloseCustomInput = () => {
    setShowCustomInput(false);
    setAnchorEl(null);
    setCustomValue('');
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ required: isRequired ? `${field.label} is required` : false }}
      render={({ field: formField, fieldState: { error } }) => (
        <Box>
          <FormControl fullWidth error={!!error}>
            <InputLabel>{field.label} {isRequired && '*'}</InputLabel>
            <Select
              {...formField}
              label={`${field.label} ${isRequired ? '*' : ''}`}
              value={formField.value || ''}
            >
              {allOptions.map((option, optionIndex) => (
                <MenuItem key={optionIndex} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography sx={{ flexGrow: 1 }}>
                      {option.label}
                    </Typography>
                    {option.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}

              {/* Add Custom Option */}
              <MenuItem
                onClick={handleShowCustomInput}
                sx={{
                  borderTop: allOptions.length > 0 ? '1px solid #eee' : 'none',
                  fontStyle: 'italic',
                  color: 'primary.main'
                }}
              >
                <AddIcon sx={{ mr: 1 }} fontSize="small" />
                Add custom option...
              </MenuItem>
            </Select>
            {error && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {error.message}
              </Typography>
            )}
          </FormControl>

          {/* Custom Input Menu */}
          <Menu
            anchorEl={anchorEl}
            open={showCustomInput}
            onClose={handleCloseCustomInput}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: { minWidth: 300, p: 2 }
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Add Custom Option
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="New option value"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddCustomOption(customValue, formField.onChange);
                }
              }}
              disabled={isUpdatingSchema}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={handleCloseCustomInput}
                disabled={isUpdatingSchema}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleAddCustomOption(customValue, formField.onChange)}
                disabled={!customValue.trim() || isUpdatingSchema}
                startIcon={isUpdatingSchema ? <CircularProgress size={16} /> : <AddIcon />}
              >
                {isUpdatingSchema ? 'Adding...' : 'Add Option'}
              </Button>
            </Box>
          </Menu>
        </Box>
      )}
    />
  );
};

// Array Schema Field Component
const ArraySchemaFieldInput = ({ field, control, errors }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpanded = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <Controller
      name={field.name}
      control={control}
      rules={{ required: field.required ? `${field.label} is required` : false }}
      render={({ field: formField, fieldState: { error } }) => {
        const currentValue = formField.value || [];

        const addNewRow = () => {
          const newRow = {};
          field.arraySchema.forEach(schemaField => {
            newRow[schemaField.name] = getDefaultValue(schemaField.type);
          });
          formField.onChange([...currentValue, newRow]);
        };

        const removeRow = (index) => {
          const newValue = currentValue.filter((_, i) => i !== index);
          formField.onChange(newValue);
        };

        const updateRowField = (rowIndex, fieldName, value) => {
          const newValue = [...currentValue];
          newValue[rowIndex] = { ...newValue[rowIndex], [fieldName]: value };
          formField.onChange(newValue);
        };

        const getDefaultValue = (type) => {
          switch (type) {
            case 'string':
            case 'email':
            case 'url':
            case 'text':
              return '';
            case 'number':
              return 0;
            case 'boolean':
              return false;
            case 'date':
              return null;
            default:
              return '';
          }
        };

        const renderFieldInput = (schemaField, rowIndex, value) => {
          const fieldId = `${field.name}.${rowIndex}.${schemaField.name}`;

          switch (schemaField.type) {
            case 'string':
            case 'email':
            case 'url':
              return (
                <TextField
                  fullWidth
                  size="small"
                  type={schemaField.type === 'email' ? 'email' : schemaField.type === 'url' ? 'url' : 'text'}
                  value={value || ''}
                  onChange={(e) => updateRowField(rowIndex, schemaField.name, e.target.value)}
                  placeholder={schemaField.label}
                />
              );
            case 'text':
              return (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={value || ''}
                  onChange={(e) => updateRowField(rowIndex, schemaField.name, e.target.value)}
                  placeholder={schemaField.label}
                />
              );
            case 'number':
              return (
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  value={value || 0}
                  onChange={(e) => updateRowField(rowIndex, schemaField.name, parseFloat(e.target.value) || 0)}
                  placeholder={schemaField.label}
                />
              );
            case 'boolean':
              return (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value || false}
                      onChange={(e) => updateRowField(rowIndex, schemaField.name, e.target.checked)}
                    />
                  }
                  label={schemaField.label}
                />
              );
            case 'date':
              return (
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={value ? (value instanceof Date ? value.toISOString().split('T')[0] : value.split('T')[0]) : ''}
                  onChange={(e) => updateRowField(rowIndex, schemaField.name, e.target.value)}
                  placeholder={schemaField.label}
                  InputLabelProps={{ shrink: true }}
                />
              );
            default:
              return (
                <TextField
                  fullWidth
                  size="small"
                  value={value || ''}
                  onChange={(e) => updateRowField(rowIndex, schemaField.name, e.target.value)}
                  placeholder={schemaField.label}
                />
              );
          }
        };

        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">
                {field.label} {field.required && '*'}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addNewRow}
              >
                Add Row
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message}
              </Alert>
            )}

            {currentValue.length === 0 ? (
              <Alert severity="info">
                No rows added yet. Click "Add Row" to add data.
              </Alert>
            ) : (
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {currentValue.map((row, rowIndex) => (
                  <Box key={rowIndex} sx={{ borderBottom: rowIndex < currentValue.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                    {/* Row Header */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'grey.50',
                      cursor: 'pointer'
                    }} onClick={() => toggleRowExpanded(rowIndex)}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Row {rowIndex + 1}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRow(rowIndex);
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <IconButton size="small">
                          {expandedRows.has(rowIndex) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Row Content */}
                    <Collapse in={expandedRows.has(rowIndex)}>
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          {field.arraySchema.map((schemaField) => (
                            <Grid item xs={12} sm={6} key={schemaField.name}>
                              <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                                {schemaField.label} ({schemaField.type})
                              </Typography>
                              {renderFieldInput(schemaField, rowIndex, row[schemaField.name])}
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Box>
                ))}
              </Box>
            )}

            {field.description && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {field.description}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};

// Array Field Input Component
const ArrayFieldInput = ({ field, control }) => {
  const [newItem, setNewItem] = useState('');

  return (
    <Controller
      name={field.name}
      control={control}
      rules={{ required: field.required ? `${field.label} is required` : false }}
      render={({ field: formField, fieldState: { error } }) => {
        const addItem = () => {
          if (newItem.trim()) {
            const currentValue = formField.value || [];
            formField.onChange([...currentValue, newItem.trim()]);
            setNewItem('');
          }
        };

        const removeItem = (indexToRemove) => {
          const currentValue = formField.value || [];
          formField.onChange(currentValue.filter((_, index) => index !== indexToRemove));
        };

        const handleKeyPress = (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            addItem();
          }
        };

        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              {field.label} {field.required ? '*' : ''}
            </Typography>

            {/* Current items display */}
            {formField.value && formField.value.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formField.value.map((item, index) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={item}
                    onDelete={() => removeItem(index)}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Box>
            )}

            {/* Add new item */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter new item"
                error={!!error}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={addItem}
                disabled={!newItem.trim()}
                sx={{ minWidth: '60px', height: '40px' }}
              >
                <AddIcon />
              </Button>
            </Box>

            {(error?.message || field.description) && (
              <Typography
                variant="caption"
                color={error ? 'error' : 'textSecondary'}
                sx={{ mt: 0.5, display: 'block' }}
              >
                {error?.message || field.description}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};

const DataRecordDialog = ({ open, onClose, schema, schemas = [], record, onSave, onRecordSaved }) => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(schema);
  const [quickFillOpen, setQuickFillOpen] = useState(false);
  const [quickFillJson, setQuickFillJson] = useState('');
  const [quickFillErrors, setQuickFillErrors] = useState([]);
  const [quickFillWarnings, setQuickFillWarnings] = useState([]);
  const [parsedRecords, setParsedRecords] = useState([]);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [savingRecords, setSavingRecords] = useState(new Set());

  const handleSchemaUpdate = (updatedSchema) => {
    setSelectedSchema(updatedSchema);
  };

  const { control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {}
  });

  useEffect(() => {
    if (open) {
      // Reset Quick Fill state
      setQuickFillOpen(false);
      setQuickFillJson('');
      setQuickFillErrors([]);
      setQuickFillWarnings([]);
      setParsedRecords([]);
      setExpandedRecord(null);
      setSavingRecords(new Set());

      // Determine which schema to use
      let currentSchema = schema;
      if (!currentSchema && schemas.length > 0) {
        currentSchema = schemas[0];
      }
      setSelectedSchema(currentSchema);

      if (currentSchema) {
        // Prepare default values
        const defaultValues = {
          status: record ? record.status : 'published'
        };

        // Add field values
        currentSchema.fields.forEach(field => {
          if (record?.data) {
            // Editing existing record
            let value = record.data[field.name];

            // Handle date fields
            if (field.type === 'date' && value) {
              try {
                value = new Date(value);
              } catch (error) {
                console.warn(`Invalid date value for field ${field.name}:`, value);
                value = null;
              }
            }

            // Handle array fields
            if (field.type === 'array' && !Array.isArray(value)) {
              value = value ? [value] : [];
            }

            // Handle object_array fields
            if (field.type === 'object_array' && !Array.isArray(value)) {
              value = [];
            }

            defaultValues[field.name] = value !== undefined ? value : getDefaultFieldValue(field);
          } else {
            // For new records, don't set default value for ID field (it will be generated)
            if (field.name !== 'id') {
              defaultValues[field.name] = getDefaultFieldValue(field);
            }
          }
        });

        reset(defaultValues);
      }
    }
  }, [open, schema, schemas, record, reset]);

  const handleSchemaChange = (newSchemaId) => {
    const newSchema = schemas.find(s => s._id === newSchemaId);
    setSelectedSchema(newSchema);

    if (newSchema) {
      // Reset form with new schema's default values
      const defaultValues = {
        status: 'published'
      };

      newSchema.fields.forEach(field => {
        // For new records, don't set default value for ID field (it will be generated)
        if (field.name !== 'id') {
          defaultValues[field.name] = getDefaultFieldValue(field);
        }
      });

      reset(defaultValues);
    }
  };

  const getDefaultFieldValue = (field) => {
    if (field.defaultValue !== null && field.defaultValue !== undefined) {
      return field.defaultValue;
    }

    switch (field.type) {
      case 'string':
      case 'email':
      case 'url':
      case 'text':
        return '';
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'date':
        return null;
      case 'array':
        return [];
      case 'object':
        return {};
      case 'object_array':
        return [];
      case 'dropdown':
        // Return the default option if available, otherwise empty string
        const defaultOption = field.options?.find(opt => opt.isDefault);
        return defaultOption ? defaultOption.value : '';
      default:
        return '';
    }
  };

  const handleQuickFill = () => {
    setQuickFillErrors([]);
    setQuickFillWarnings([]);
    setParsedRecords([]);

    if (!quickFillJson.trim()) {
      setQuickFillErrors(['Please enter JSON data']);
      return;
    }

    try {
      const jsonData = JSON.parse(quickFillJson);

      // Check if it's an array (bulk import) or single object
      if (Array.isArray(jsonData)) {
        // Handle bulk import
        handleBulkImport(jsonData);
      } else if (typeof jsonData === 'object' && jsonData !== null) {
        // Handle single record import (existing functionality)
        handleSingleRecordImport(jsonData);
      } else {
        setQuickFillErrors(['JSON data must be an object or an array of objects']);
        return;
      }

    } catch (parseError) {
      setQuickFillErrors(['Invalid JSON format. Please check your JSON syntax.']);
    }
  };

  const handleSingleRecordImport = (jsonData) => {
    const errors = [];
    const warnings = [];
    const formData = {};

    // Get schema fields excluding system-generated fields
    const fillableFields = selectedSchema.fields.filter(field =>
      field.name !== 'id' || record // Include ID only if editing existing record
    );

    // Check for missing required fields
    fillableFields.forEach(field => {
      if (field.name === 'id' && !record) {
        return; // Skip ID for new records
      }

      if (!(field.name in jsonData)) {
        if (field.required) {
          warnings.push(`Required field "${field.label}" (${field.name}) is missing from your data`);
        } else {
          warnings.push(`Optional field "${field.label}" (${field.name}) is missing from your data`);
        }
        return;
      }

      const value = jsonData[field.name];

      try {
        // Convert and validate the value based on field type
        const convertedValue = convertValueForField(value, field);
        formData[field.name] = convertedValue;
      } catch (conversionError) {
        errors.push(`Field "${field.label}" (${field.name}): ${conversionError.message}`);
      }
    });

    // Check for extra fields in JSON that don't exist in schema
    Object.keys(jsonData).forEach(key => {
      if (!fillableFields.find(field => field.name === key)) {
        warnings.push(`Field "${key}" exists in your data but not in the schema`);
      }
    });

    if (errors.length > 0) {
      setQuickFillErrors(errors);
      return;
    }

    // Apply the converted data to the form
    Object.entries(formData).forEach(([fieldName, value]) => {
      setValue(fieldName, value);
    });

    if (warnings.length > 0) {
      setQuickFillWarnings(warnings);
    }

    // Clear the JSON input and show success message
    setQuickFillJson('');
    toast.success(`Successfully filled ${Object.keys(formData).length} fields`);
  };

  const handleBulkImport = (jsonArray) => {
    const errors = [];
    const warnings = [];
    const processedRecords = [];

    // Get schema fields excluding system-generated fields
    const fillableFields = selectedSchema.fields.filter(field => field.name !== 'id');

    jsonArray.forEach((recordData, recordIndex) => {
      if (typeof recordData !== 'object' || recordData === null) {
        errors.push(`Record ${recordIndex + 1}: Must be an object, not ${typeof recordData}`);
        return;
      }

      const recordErrors = [];
      const recordWarnings = [];
      const convertedData = {};

      // Process each field
      fillableFields.forEach(field => {
        if (field.name in recordData) {
          try {
            const convertedValue = convertValueForField(recordData[field.name], field);
            convertedData[field.name] = convertedValue;
          } catch (conversionError) {
            recordErrors.push(`${field.label}: ${conversionError.message}`);
          }
        } else {
          if (field.required) {
            recordErrors.push(`Required field "${field.label}" is missing`);
          }
          // Set default value for missing fields
          convertedData[field.name] = getDefaultFieldValue(field);
        }
      });

      // Check for extra fields
      Object.keys(recordData).forEach(key => {
        if (!fillableFields.find(field => field.name === key)) {
          recordWarnings.push(`Field "${key}" not in schema`);
        }
      });

      processedRecords.push({
        index: recordIndex,
        originalData: recordData,
        convertedData,
        errors: recordErrors,
        warnings: recordWarnings,
        isValid: recordErrors.length === 0
      });
    });

    if (processedRecords.length === 0) {
      setQuickFillErrors(['No valid records found in the array']);
      return;
    }

    setParsedRecords(processedRecords);

    const validRecords = processedRecords.filter(r => r.isValid).length;
    const invalidRecords = processedRecords.length - validRecords;

    if (invalidRecords > 0) {
      setQuickFillWarnings([`${validRecords} valid records found, ${invalidRecords} records have errors`]);
    } else {
      setQuickFillWarnings([`${validRecords} valid records ready to import`]);
    }

    toast.success(`Parsed ${processedRecords.length} records (${validRecords} valid)`);
  };

  const handleSaveSingleRecord = async (recordData, recordIndex) => {
    if (savingRecords.has(recordIndex)) return;

    setSavingRecords(prev => new Set([...prev, recordIndex]));

    try {
      const submitData = {
        data: recordData.convertedData,
        status: 'published'
      };

      await dataAPI.createRecord(selectedSchema._id, submitData);

      // Remove the saved record from the list
      setParsedRecords(prev => prev.filter(r => r.index !== recordIndex));

      toast.success(`Record ${recordIndex + 1} saved successfully`);

      // Trigger refresh of parent data
      onRecordSaved?.();

    } catch (error) {
      console.error('Error saving record:', error);
      toast.error(`Failed to save record ${recordIndex + 1}: ${error.response?.data?.message || error.message}`);
    } finally {
      setSavingRecords(prev => {
        const newSet = new Set(prev);
        newSet.delete(recordIndex);
        return newSet;
      });
    }
  };

  const handleSaveAllRecords = async () => {
    const validRecords = parsedRecords.filter(r => r.isValid);

    if (validRecords.length === 0) {
      toast.error('No valid records to save');
      return;
    }

    // Mark all records as saving
    const recordIndexes = validRecords.map(r => r.index);
    setSavingRecords(new Set(recordIndexes));

    try {
      // Prepare bulk data
      const bulkData = validRecords.map(recordData => ({
        data: recordData.convertedData,
        status: 'published'
      }));

      // Call bulk API
      const response = await dataAPI.createRecordsBulk(selectedSchema._id, bulkData);

      // Show success message with details
      const { successCount, errorCount, totalProcessed } = response.data;

      if (successCount === totalProcessed) {
        toast.success(`All ${successCount} records saved successfully!`);
      } else if (successCount > 0) {
        toast.success(`${successCount} records saved successfully. ${errorCount} failed.`);
      } else {
        toast.error(`Failed to save any records. ${errorCount} errors occurred.`);
      }

      // Remove successfully saved records from the list
      if (successCount > 0) {
        setParsedRecords(prev => prev.filter(r => !r.isValid));

        // Trigger refresh of parent data
        onRecordSaved?.();
      }

      // Log any errors for debugging
      if (response.data.errors?.length > 0) {
        console.warn('Bulk insert errors:', response.data.errors);
      }
      if (response.data.validationErrors?.length > 0) {
        console.warn('Bulk insert validation errors:', response.data.validationErrors);
      }

    } catch (error) {
      console.error('Error saving records in bulk:', error);

      // Show detailed error information
      if (error.response?.data) {
        const { message, errors, validationErrors } = error.response.data;
        console.error('Backend response:', error.response.data);

        let errorMessage = message || 'Failed to save records';

        // Add specific error details
        if (errors?.length > 0) {
          console.error('Processing errors:', errors);
          errorMessage += `\nProcessing errors: ${errors.map(e => `Record ${e.recordIndex + 1}: ${e.error}`).join(', ')}`;
        }

        if (validationErrors?.length > 0) {
          console.error('Validation errors:', validationErrors);
          errorMessage += `\nValidation errors: ${validationErrors.map(e => `Record ${e.recordIndex + 1}: ${e.errors.join(', ')}`).join(', ')}`;
        }

        toast.error(errorMessage);
      } else {
        toast.error(`Failed to save records: ${error.message}`);
      }
    } finally {
      // Clear saving state
      setSavingRecords(new Set());
    }
  };

  const convertValueForField = (value, field) => {
    if (value === null || value === undefined || value === '') {
      if (field.required) {
        throw new Error('This field is required');
      }
      return getDefaultFieldValue(field);
    }

    switch (field.type) {
      case 'string':
      case 'email':
      case 'url':
      case 'text':
        return String(value);

      case 'number':
        const num = parseFloat(value);
        if (isNaN(num)) {
          throw new Error('Must be a valid number');
        }
        return num;

      case 'boolean':
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const lower = value.toLowerCase();
          if (lower === 'true' || lower === '1' || lower === 'yes') return true;
          if (lower === 'false' || lower === '0' || lower === 'no') return false;
          throw new Error('Must be true/false, 1/0, or yes/no');
        }
        if (typeof value === 'number') return value !== 0;
        return Boolean(value);

      case 'date':
        if (value instanceof Date) return value;
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          throw new Error('Must be a valid date');
        }
        return date;

      case 'array':
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
            return [value]; // Treat as single item array
          } catch {
            return [value]; // Treat as single item array
          }
        }
        return [value]; // Wrap single value in array

      case 'object':
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return value;
        }
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              return parsed;
            }
            throw new Error('Must be a valid JSON object');
          } catch {
            throw new Error('Must be a valid JSON object');
          }
        }
        throw new Error('Must be an object');

      case 'object_array':
        if (Array.isArray(value)) {
          // Validate that all items are objects
          for (let i = 0; i < value.length; i++) {
            if (typeof value[i] !== 'object' || value[i] === null || Array.isArray(value[i])) {
              throw new Error(`Item ${i + 1} must be an object`);
            }
          }
          return value;
        }
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
              // Validate that all items are objects
              for (let i = 0; i < parsed.length; i++) {
                if (typeof parsed[i] !== 'object' || parsed[i] === null || Array.isArray(parsed[i])) {
                  throw new Error(`Item ${i + 1} must be an object`);
                }
              }
              return parsed;
            }
            throw new Error('Must be an array of objects');
          } catch {
            throw new Error('Must be a valid JSON array of objects');
          }
        }
        throw new Error('Must be an array of objects');

      case 'dropdown':
        const stringValue = String(value);
        const validOption = field.options?.find(opt =>
          opt.value === stringValue || opt.label === stringValue
        );
        if (!validOption) {
          throw new Error(`Must be one of: ${field.options?.map(opt => opt.value).join(', ') || 'No options available'}`);
        }
        return validOption.value;

      default:
        return String(value);
    }
  };

  const onSubmit = async (data) => {
    try {
      setValidationErrors([]);

      if (!selectedSchema) {
        setValidationErrors(['Please select a schema']);
        return;
      }

      // Validate required fields
      const errors = [];
      selectedSchema.fields.forEach(field => {
        // Skip ID field for new records
        if (field.name === 'id' && !record) {
          return;
        }

        if (field.required && (data[field.name] === undefined || data[field.name] === null || data[field.name] === '')) {
          errors.push(`${field.label} is required`);
        }
      });

      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Prepare the data for submission
      const submitData = {
        data: {},
        status: data.status
      };

      // Add field data, excluding the ID field for new records
      selectedSchema.fields.forEach(field => {
        if (field.name === 'id' && !record) {
          return; // Skip ID for new records, it will be auto-generated
        }

        let value = data[field.name];

        // Handle date fields
        if (field.type === 'date' && value instanceof Date) {
          value = value.toISOString();
        }

        submitData.data[field.name] = value;
      });

      if (record) {
        // Update existing record
        await dataAPI.updateRecord(record._id, submitData);
        toast.success('Record updated successfully');
      } else {
        // Create new record
        await dataAPI.createRecord(selectedSchema._id, submitData);
        toast.success('Record created successfully');
      }

      if (onSave) {
        onSave();
      }

      // Trigger refresh callback
      if (onRecordSaved) {
        onRecordSaved();
      }

      onClose();
    } catch (error) {
      console.error('Error saving record:', error);
      toast.error(error.response?.data?.message || 'Failed to save record');
    }
  };

  const renderFieldInput = (field) => {
    switch (field.type) {
      case 'string':
      case 'email':
      case 'url':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{ required: field.required ? `${field.label} is required` : false }}
            render={({ field: formField, fieldState: { error } }) => (
              <TextField
                {...formField}
                fullWidth
                label={`${field.label} ${field.required ? '*' : ''}`}
                type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
                error={!!error}
                helperText={error?.message || field.description}
                value={formField.value || ''}
              />
            )}
          />
        );

      case 'text':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{ required: field.required ? `${field.label} is required` : false }}
            render={({ field: formField, fieldState: { error } }) => (
              <TextField
                {...formField}
                fullWidth
                multiline
                rows={4}
                label={`${field.label} ${field.required ? '*' : ''}`}
                error={!!error}
                helperText={error?.message || field.description}
                value={formField.value || ''}
              />
            )}
          />
        );

      case 'number':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{ required: field.required ? `${field.label} is required` : false }}
            render={({ field: formField, fieldState: { error } }) => (
              <TextField
                {...formField}
                fullWidth
                type="number"
                label={`${field.label} ${field.required ? '*' : ''}`}
                error={!!error}
                helperText={error?.message || field.description}
                value={formField.value || ''}
                onChange={(e) => formField.onChange(parseFloat(e.target.value) || 0)}
              />
            )}
          />
        );

      case 'boolean':
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: formField }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={formField.value || false}
                    onChange={formField.onChange}
                  />
                }
                label={`${field.label} ${field.required ? '*' : ''}`}
              />
            )}
          />
        );

      case 'date':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{ required: field.required ? `${field.label} is required` : false }}
            render={({ field: formField, fieldState: { error } }) => (
              <TextField
                {...formField}
                fullWidth
                type="date"
                label={`${field.label} ${field.required ? '*' : ''}`}
                error={!!error}
                helperText={error?.message || field.description}
                value={formField.value ? (formField.value instanceof Date ? formField.value.toISOString().split('T')[0] : formField.value.split('T')[0]) : ''}
                onChange={(e) => formField.onChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        );

      case 'array':
        return (
          <ArrayFieldInput
            field={field}
            control={control}
          />
        );

      case 'object':
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{ required: field.required ? `${field.label} is required` : false }}
            render={({ field: formField, fieldState: { error } }) => (
              <TextField
                {...formField}
                fullWidth
                multiline
                rows={4}
                label={`${field.label} ${field.required ? '*' : ''} (JSON)`}
                placeholder='{"key": "value"}'
                error={!!error}
                helperText={error?.message || field.description || 'Enter valid JSON object'}
                value={typeof formField.value === 'object' ? JSON.stringify(formField.value, null, 2) : formField.value || ''}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    formField.onChange(parsed);
                  } catch {
                    formField.onChange(e.target.value);
                  }
                }}
              />
            )}
          />
        );

      case 'dropdown':
        return <DropdownFieldInput field={field} control={control} errors={errors} schema={selectedSchema} onSchemaUpdate={handleSchemaUpdate} />;

      case 'object_array':
        return <ArraySchemaFieldInput field={field} control={control} errors={errors} />;

      default:
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{ required: field.required ? `${field.label} is required` : false }}
            render={({ field: formField, fieldState: { error } }) => (
              <TextField
                {...formField}
                fullWidth
                label={`${field.label} ${field.required ? '*' : ''}`}
                error={!!error}
                helperText={error?.message || field.description}
                value={formField.value || ''}
              />
            )}
          />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={window.innerWidth < 600}
    >
      <DialogTitle>
        {record ? `Edit Record` : 'Add New Record'}
        {selectedSchema && (
          <Typography variant="subtitle2" color="textSecondary">
            Schema: {selectedSchema.name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <form>
          <Grid container spacing={3}>
            {/* Schema Selection - only show if multiple schemas available and not editing */}
            {!record && schemas.length > 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Schema</InputLabel>
                  <Select
                    value={selectedSchema?._id || ''}
                    onChange={(e) => handleSchemaChange(e.target.value)}
                    label="Select Schema"
                  >
                    {schemas.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Quick Fill */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent
                  sx={{ cursor: 'pointer', py: 2 }}
                  onClick={() => setQuickFillOpen(!quickFillOpen)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="primary">
                      Quick Fill from JSON
                    </Typography>
                    <IconButton size="small">
                      {quickFillOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Import data from JSON (single record or bulk array)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Fill Collapsed Content */}
            <Collapse in={quickFillOpen}>
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Paste a JSON object to automatically fill matching schema fields, or paste an array of objects for bulk import. System fields like 'id' will be ignored for new records.
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    placeholder={`Single Record:
{
  "name": "Product 1",
  "description": "Sample product description",
  "price": 100
}

Bulk Import (Array):
[
  {
    "name": "Product 1",
    "description": "First product",
    "price": 100
  },
  {
    "name": "Product 2", 
    "description": "Second product",
    "price": 200
  },
  {
    "name": "Product 3",
    "description": "Third product", 
    "price": 300
  }
]

Note: Make sure your schema has fields that match the JSON data you're importing. The system will validate each field against your schema definition.`}
                    value={quickFillJson}
                    onChange={(e) => setQuickFillJson(e.target.value)}
                    variant="outlined"
                    size="small"
                  />

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleQuickFill}
                      disabled={!quickFillJson.trim()}
                      startIcon={<AutoAwesomeIcon />}
                    >
                      Fill Fields
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setQuickFillJson('');
                        setQuickFillErrors([]);
                        setQuickFillWarnings([]);
                        setParsedRecords([]);
                        setExpandedRecord(null);
                      }}
                    >
                      Clear
                    </Button>
                  </Box>

                  {/* Quick Fill Errors */}
                  {quickFillErrors.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Errors:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {quickFillErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Quick Fill Warnings */}
                  {quickFillWarnings.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Warnings:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {quickFillWarnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Bulk Import Records List */}
                  {parsedRecords.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="primary.main">
                          Parsed Records ({parsedRecords.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSaveAllRecords}
                            disabled={parsedRecords.filter(r => r.isValid).length === 0}
                            startIcon={<AutoAwesomeIcon />}
                          >
                            Save All Valid ({parsedRecords.filter(r => r.isValid).length})
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setParsedRecords([]);
                              setExpandedRecord(null);
                            }}
                          >
                            Clear List
                          </Button>
                        </Box>
                      </Box>

                      {/* Records List */}
                      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {parsedRecords.map((recordData) => (
                          <Card key={recordData.index} variant="outlined" sx={{ mb: 2 }}>
                            <CardContent sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="subtitle2">
                                      Record {recordData.index + 1}
                                    </Typography>
                                    <Chip
                                      label={recordData.isValid ? 'Valid' : 'Has Errors'}
                                      size="small"
                                      color={recordData.isValid ? 'success' : 'error'}
                                      variant="outlined"
                                    />
                                    {recordData.warnings.length > 0 && (
                                      <Chip
                                        label={`${recordData.warnings.length} Warning${recordData.warnings.length > 1 ? 's' : ''}`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>

                                  {/* Preview of record data */}
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {Object.entries(recordData.convertedData).slice(0, 3).map(([key, value]) => {
                                      const field = selectedSchema.fields.find(f => f.name === key);
                                      const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                                      return (
                                        <Box key={key} sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                                          <Typography variant="caption">
                                            <strong>{field?.label || key}:</strong> {displayValue.substring(0, 30)}{displayValue.length > 30 ? '...' : ''}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                    {Object.keys(recordData.convertedData).length > 3 && (
                                      <Typography variant="caption" color="textSecondary">
                                        +{Object.keys(recordData.convertedData).length - 3} more fields
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Errors display */}
                                  {recordData.errors.length > 0 && (
                                    <Alert severity="error" sx={{ mb: 1 }}>
                                      <Typography variant="caption">
                                        <strong>Errors:</strong> {recordData.errors.join(', ')}
                                      </Typography>
                                    </Alert>
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => setExpandedRecord(expandedRecord === recordData.index ? null : recordData.index)}
                                  >
                                    {expandedRecord === recordData.index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </IconButton>

                                  {recordData.isValid && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleSaveSingleRecord(recordData, recordData.index)}
                                      disabled={savingRecords.has(recordData.index)}
                                      startIcon={savingRecords.has(recordData.index) ? <CircularProgress size={16} /> : <AddIcon />}
                                    >
                                      {savingRecords.has(recordData.index) ? 'Saving...' : 'Save'}
                                    </Button>
                                  )}
                                </Box>
                              </Box>

                              {/* Expanded details */}
                              <Collapse in={expandedRecord === recordData.index}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  All Field Data:
                                </Typography>
                                <Grid container spacing={2}>
                                  {selectedSchema.fields.filter(f => f.name !== 'id').map((field) => {
                                    const value = recordData.convertedData[field.name];
                                    const displayValue = value === null || value === undefined ? 'Not set' :
                                      typeof value === 'object' ? JSON.stringify(value, null, 2) :
                                        String(value);

                                    return (
                                      <Grid item xs={12} sm={6} key={field.name}>
                                        <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                                            {field.label} ({field.type})
                                          </Typography>
                                          <Typography variant="body2" sx={{
                                            fontFamily: typeof value === 'object' ? 'monospace' : 'inherit',
                                            whiteSpace: typeof value === 'object' ? 'pre-wrap' : 'normal',
                                            wordBreak: 'break-word'
                                          }}>
                                            {displayValue}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>

                                {recordData.warnings.length > 0 && (
                                  <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="caption">
                                      <strong>Warnings:</strong> {recordData.warnings.join(', ')}
                                    </Typography>
                                  </Alert>
                                )}
                              </Collapse>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Collapse>

            {/* Form Validation Errors */}
            {validationErrors.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="subtitle2" gutterBottom>
                    Please fix the following errors:
                  </Typography>
                  <ul>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              </Grid>
            )}

            {/* Status Field */}
            <Grid item xs={12}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Dynamic Fields */}
            {selectedSchema?.fields?.filter(field => field.name !== 'id').map((field) => (
              <Grid item xs={12} sm={field.type === 'text' || field.type === 'object' || field.type === 'object_array' ? 12 : 6} key={field.name}>
                {renderFieldInput(field)}
              </Grid>
            ))}
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Saving...' : record ? 'Update Record' : 'Create Record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DataRecordDialog;